import { StyleSheet, View } from 'react-native';
import { IconButton } from '../index';

import type { useTheme } from './theme';
import type { CardHints } from './cardHints';
import type { HoldProps } from './useTransientTip';
import { TitleButton } from './TitleButton';
import { PositionCue } from './PositionCue';

// #308, row 1 of the logger card: the exercise name, then ? how-to, target,
// the Pin (logger) or the week cue (editor), and the trash. The name IS the
// button that opens the exercise picker (Antenore on v2: «il titolo è un
// bottone, premuto apre l'exercise picker, parla da sé»), so the replace icon
// is gone. It must READ as a button: a bordered field with a chevron, the look
// of the editor's Select field. A long name scrolls inside it (MarqueeText).
//
// Icons are 36 with a gap of 8: a hitSlop of 4 on each side reaches 44
// without two touch zones meeting on the same pixels.
//
// The Pin is a pushpin, not the star: the star already means "favourite /
// default" three times in the app (picker, machine, gym), and this pin means
// something else. Off is the outline, on is SOLID on the active tint: the
// shape says the state, the colour repeats it. Announced as a switch.
export type WeekCue = {
  index: number;
  count: number;
  onNext: () => void;
  onPrev: () => void;
};

export function RosterTitleRow({
  title,
  onSwap,
  onOpenTip,
  target,
  weekCue,
  pin,
  onRemoveExercise,
  hold,
  hints,
  t,
  theme,
}: {
  title: string;
  /** Opens the swap picker for the active exercise. */
  onSwap: () => void;
  /** Opens the how-to quick-tip for the active exercise. */
  onOpenTip: () => void;
  /** The weekly target rows. Absent when there are none to show. */
  target?: { expanded: boolean; onToggle: () => void };
  /** PE-31, prescribe only: the week cursor. ⛔ Never on a one-week program. */
  weekCue?: WeekCue;
  /** #308, live logging only: the Pin. The week cue takes its slot in the
   *  editor, where there is no rest to come back from. */
  pin?: { pinned: boolean; onToggle: () => void };
  onRemoveExercise: () => void;
  /** Every control here explains itself when held (#308). */
  hold: HoldProps;
  hints: CardHints;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const slop = { top: 4, bottom: 4, left: 4, right: 4 };
  return (
    <View style={styles.row}>
      <TitleButton
        text={title}
        accessibilityLabel={`${title}. ${t('mobile.logger.replace', {
          defaultValue: 'Replace',
        })}`}
        onPress={onSwap}
        gesture={hold(hints.title).gesture}
        accessibilityHint={hints.title}
        hitSlop={slop}
        style={styles.titleSlot}
        theme={theme}
      />
      <IconButton
        name="help"
        onPress={onOpenTip}
        accessibilityLabel={t('mobile.logger.descriptionToggle', {
          defaultValue: 'Show / hide description',
        })}
        {...hold(hints.howTo)}
        hitSlop={slop}
        theme={theme}
      />
      {target ? (
        <IconButton
          name="target"
          active={target.expanded}
          expanded={target.expanded}
          onPress={target.onToggle}
          accessibilityLabel={t('mobile.logger.targetEyebrow', {
            defaultValue: 'Target',
          })}
          {...hold(hints.target)}
          hitSlop={slop}
          theme={theme}
        />
      ) : null}
      {!weekCue && pin ? (
        <IconButton
          name="pin"
          checked={pin.pinned}
          activeGlyph="fill"
          onPress={pin.onToggle}
          accessibilityLabel={t('mobile.logger.pin', { defaultValue: 'Pin' })}
          {...hold(hints.pin(pin.pinned))}
          hitSlop={slop}
          theme={theme}
        />
      ) : null}
      {weekCue && weekCue.count > 1 ? (
        <PositionCue
          theme={theme}
          glyph="calendar"
          position={weekCue.index + 1}
          total={weekCue.count}
          hitSlop={slop}
          // ⛔ Says which AXIS out loud. "2 of 4" beside a day×week grid
          // reads as a day, and the visible "W2/4" is a notation a screen
          // reader spells rather than speaks.
          accessibilityLabel={t('composer.weekCue', {
            defaultValue: 'Week {{pos}} of {{total}}. Tap for the next week.',
            pos: weekCue.index + 1,
            total: weekCue.count,
          })}
          onPress={weekCue.onNext}
          // The way back carries no pixels: the grid's swipe, and a named
          // action so VoiceOver and a keyboard reach it too. ⛔ Not a second
          // arrow: Antenore removed the arrows on purpose. The hold shows the
          // hint like every card control (#308), so it no longer steps back.
          onPrev={weekCue.onPrev}
          {...hold(hints.week)}
          prevLabel={t('composer.weekCuePrev', { defaultValue: 'Previous week' })}
          // Same "W{n}" the grid's column heads use.
          prefix="W"
        />
      ) : null}
      <IconButton
        name="trash"
        variant="destructive"
        onPress={onRemoveExercise}
        accessibilityLabel={t('workout.removeExercise', {
          defaultValue: 'Remove exercise',
        })}
        {...hold(hints.trash)}
        hitSlop={{ ...slop, right: 8 }}
        theme={theme}
      />
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // Never wraps: the title absorbs every pixel the icons leave. Centred,
    // not top-aligned: with no vertical padding the field is 36 on one line
    // and 38 on two (2 x 18 + border), so the icons sit 1 px off its middle
    // and the row grows by 2 at most.
    row: { flexDirection: 'row', alignItems: 'center', gap: s.s3 },
    titleSlot: { flex: 1, minWidth: 0 },
  });
}
